import { useReducer } from "react"
import pThrottle from "p-throttle"

import { useLogger } from "@/hooks/useLogger"

import { useMutationCheckRows, useMutationWriteRow } from "./no-call-csv-api"
import { parseNoCallCsv } from "./parse-csv"
import type {
  CheckedRow,
  ImportProgress,
  InvalidCheckedRow,
  InvalidParsedRow,
  MergedRow,
  ValidParsedRow,
} from "./schemas"

type ImportPhase =
  | "checking"
  | "completed"
  | "idle"
  | "importing"
  | "parsing_error"
  | "parsing"
  | "preview"

interface CheckProgress {
  completed: number
  total: number
}

interface State {
  checkProgress: CheckProgress | null
  filename: string | null
  parseErrors: string[]
  phase: ImportPhase
  rows: MergedRow[]
}

function createInitialState(): State {
  return {
    checkProgress: null,
    filename: null,
    parseErrors: [],
    phase: "idle",
    rows: [],
  }
}

type Action =
  | { completed: number; total: number; type: "check_progress" }
  | { errorMessages: string[]; rowIndex: number; type: "write_error" }
  | { errorMessages: string[]; type: "parse_failed" }
  | { filename: string; type: "select_file" }
  | { rowIndex: number; type: "write_success" }
  | { rows: MergedRow[]; type: "check_complete" }
  | { type: "parsing" }
  | { type: "reset" }
  | { type: "start_import" }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "select_file":
      return {
        ...state,
        checkProgress: null,
        filename: action.filename,
        parseErrors: [],
        phase: "parsing",
        rows: [],
      }
    case "parsing":
      return { ...state, phase: "parsing" }
    case "parse_failed":
      return {
        ...state,
        parseErrors: action.errorMessages,
        phase: "parsing_error",
      }
    case "check_progress":
      return {
        ...state,
        checkProgress: { completed: action.completed, total: action.total },
        phase: "checking",
      }
    case "check_complete":
      return {
        ...state,
        checkProgress: null,
        phase: "preview",
        rows: action.rows,
      }
    case "start_import":
      return { ...state, phase: "importing" }
    case "write_success": {
      const rows = updateRowWriteStatus(state.rows, action.rowIndex, {
        writeStatus: "success",
      })

      return {
        ...state,
        phase: isAllWritesDone(rows) ? "completed" : state.phase,
        rows,
      }
    }
    case "write_error": {
      const rows = updateRowWriteStatus(state.rows, action.rowIndex, {
        writeErrors: action.errorMessages,
        writeStatus: "error",
      })

      return {
        ...state,
        phase: isAllWritesDone(rows) ? "completed" : state.phase,
        rows,
      }
    }
    case "reset":
      return createInitialState()
  }
}

function updateRowWriteStatus(
  rows: MergedRow[],
  rowIndex: number,
  changes: Partial<Pick<CheckedRow, "writeErrors" | "writeStatus">>,
): MergedRow[] {
  return rows.map((row) =>
    row.parseStatus === "valid" && row.rowIndex === rowIndex
      ? { ...row, ...changes }
      : row,
  )
}

function isAllWritesDone(rows: MergedRow[]): boolean {
  return rows
    .filter(isWriteable)
    .every((r) => r.writeStatus === "success" || r.writeStatus === "error")
}

function isWriteable(row: MergedRow): row is CheckedRow {
  return row.parseStatus === "valid" && row.status === "ready"
}

function useImportNoCallCSV() {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState)
  const log = useLogger()
  const checkMutation = useMutationCheckRows()
  const writeMutation = useMutationWriteRow()

  async function selectFile(file: File) {
    dispatch({ filename: file.name, type: "select_file" })

    const { errors, rows: parsedRows } = await parseNoCallCsv(file)

    if (errors.length) {
      log.error("Failed to parse NG LIst CSV", { parseErrors: errors })
    }

    if (errors.length || parsedRows.length === 0) {
      return dispatch({
        errorMessages: ["INVALID_CSV"],
        type: "parse_failed",
      })
    }

    const validRows = parsedRows.filter(
      (r): r is ValidParsedRow => r.parseStatus === "valid",
    )
    const invalidRows = parsedRows.filter(
      (r): r is InvalidParsedRow => r.parseStatus === "invalid",
    )

    if (validRows.length === 0) {
      return dispatch({ rows: invalidRows, type: "check_complete" })
    }

    await checkMutation.mutateAsync(
      {
        onProgress: (completed, total) =>
          dispatch({ completed, total, type: "check_progress" }),
        rows: validRows,
      },
      {
        onError: (error) => {
          log.error(error)
          dispatch({
            errorMessages: ["SERVER_CHECK_FAILED"],
            type: "parse_failed",
          })
        },
        onSuccess: (checkedRows) => {
          const merged = mergeRows(parsedRows, checkedRows)
          dispatch({ rows: merged, type: "check_complete" })
        },
      },
    )
  }

  async function startImport() {
    const writeableRows = state.rows.filter(isWriteable)

    if (writeableRows.length === 0) {
      return
    }

    const throttle = pThrottle({ interval: 1000, limit: 1, strict: true })
    dispatch({ type: "start_import" })

    for (const row of writeableRows) {
      const throttledWrite = throttle(async () =>
        writeMutation.mutateAsync(row),
      )

      try {
        const result = await throttledWrite()
        if (result.data.status === "error") {
          dispatch({
            errorMessages: [result.data.message ?? "WRITE_FAILED"],
            rowIndex: row.rowIndex,
            type: "write_error",
          })
        } else if (result.data.warnings.length) {
          dispatch({
            errorMessages: result.data.warnings,
            rowIndex: row.rowIndex,
            type: "write_error",
          })
        } else {
          dispatch({ rowIndex: row.rowIndex, type: "write_success" })
        }
      } catch (error) {
        log.error(error)
        dispatch({
          errorMessages: ["WRITE_FAILED"],
          rowIndex: row.rowIndex,
          type: "write_error",
        })
      }
    }
  }

  function reset() {
    dispatch({ type: "reset" })
  }

  function getProgress(): ImportProgress {
    const writeable = state.rows.filter(isWriteable)
    const completed = writeable.filter(
      (r) => r.writeStatus === "success" || r.writeStatus === "error",
    )
    const errors = writeable.filter((r) => r.writeStatus === "error")

    return {
      completed: completed.length,
      errors: errors.length,
      total: writeable.length,
    }
  }

  return {
    getProgress,
    reset,
    selectFile,
    startImport,
    state,
  }
}

function mergeRows(
  parsedRows: Array<{ parseStatus: string; rowIndex: number }>,
  checkedRows: CheckedRow[],
): MergedRow[] {
  const checkedByIndex = new Map(checkedRows.map((r) => [r.rowIndex, r]))

  return parsedRows.map((row) => {
    if (row.parseStatus === "invalid") {
      return row as InvalidCheckedRow
    }

    return checkedByIndex.get(row.rowIndex) ?? (row as InvalidCheckedRow)
  })
}

export { useImportNoCallCSV }
